import { useState } from 'react';
import { NavLink, Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import DeletionBanner from '../components/DeletionBanner';
import BillingPage from './account/BillingPage';
import ConnectionsPage from './account/ConnectionsPage';
import DevicesPage from './account/DevicesPage';
import OverviewPage from './account/OverviewPage';
import PrivacyPage from './account/PrivacyPage';
import SecurityPage from './account/SecurityPage';

const navigation = [
  { to: '/account/overview', label: 'Overview', glyph: '⌂' },
  { to: '/account/connections', label: 'Connections', glyph: '↗' },
  { to: '/account/security', label: 'Security', glyph: '◇' },
  { to: '/account/devices', label: 'Devices', glyph: '▣' },
  { to: '/account/billing', label: 'Billing', glyph: '○' },
  { to: '/account/data-privacy', label: 'Data & privacy', glyph: '◈' },
];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [signingOut, setSigningOut] = useState(false);

  if (!user) return null;

  async function signOut() {
    setSigningOut(true);
    await logout();
    setSigningOut(false);
  }

  return (
    <div className="account-shell">
      <a className="skip-link" href="#account-main">Skip to content</a>
      <aside className="account-sidebar">
        <div className="brand-lockup"><span className="brand-mark" aria-hidden="true">M</span><span>Meridian</span></div>
        <nav className="account-navigation" aria-label="Account center">
          {navigation.map((item) => (
            <NavLink key={item.to} to={item.to} className={({ isActive }) => isActive ? 'active' : undefined}>
              <span className="nav-glyph" aria-hidden="true">{item.glyph}</span>{item.label}
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-account">
          <span className="avatar" aria-hidden="true">{initials(user.first_name, user.last_name, user.email)}</span>
          <span className="sidebar-identity"><strong>{[user.first_name, user.last_name].filter(Boolean).join(' ') || 'Meridian account'}</strong><small>{user.email}</small></span>
          <button className="btn-link" type="button" onClick={() => void signOut()} disabled={signingOut}>{signingOut ? 'Signing out…' : 'Sign out'}</button>
        </div>
      </aside>

      <div className="account-workspace">
        <DeletionBanner />
        <main id="account-main" className="account-main" tabIndex={-1}>
          <Routes>
            <Route path="overview" element={<OverviewPage />} />
            <Route path="connections" element={<ConnectionsPage />} />
            <Route path="security" element={<SecurityPage />} />
            <Route path="devices" element={<DevicesPage />} />
            <Route path="billing" element={<BillingPage />} />
            <Route path="data-privacy" element={<PrivacyPage />} />
            {/* Absolute on purpose. A relative `to` resolves against the
                matched route, and from the catch-all splat below that means
                appending to whatever didn't match — so an unrecognized
                /account/… URL redirects to a path that doesn't match either,
                and the router loops forever instead of landing on Overview. */}
            <Route index element={<Navigate to="/account/overview" replace />} />
            <Route path="*" element={<Navigate to="/account/overview" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

function initials(firstName: string | null, lastName: string | null, email: string): string {
  const name = [firstName, lastName].filter(Boolean).map((part) => part![0]).join('');
  return (name || email[0] || 'M').toUpperCase();
}
